import type { ReactNode } from 'react';
import { Controller, useWatch, type Control, type FieldErrors } from 'react-hook-form';
import { Chip, Field, FieldGrid, InfoTip, SearchCombobox, TextArea, TextInput } from '@zarya/ui';
import { ambienteOriginText } from '@app/ambiente';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { OwnerCombobox } from '@app/shared/OwnerCombobox';
import type { ApplicationUser } from '@app/shared/owner';
import { useEnvironmentPreview, type Identity, type IdentityRole } from './Identities.api';
import {
  fieldOrigin,
  originLabel,
  textFrozen,
  type IdentityFormValues,
} from './IdentityModel';
import {
  releaseWithoutDeclarationLabel,
  releaseWithoutDeclarationTip,
} from './identityVocab';
import s from './Identities.module.css';

/**
 * Aba **Identidade** de T5 (FR-045 a FR-048).
 *
 * Os campos da atribuição são editáveis até a primeira aprovação; depois dela
 * slug, namespace, service account e imagem ficam somente leitura, com a
 * explicação do FR-048. O que não é atribuição (tipo, descoberta, SPIFFE
 * previsto) é leitura sempre.
 */

/**
 * As ajudas ⓘ eram constantes de módulo e agora saem do catálogo
 * (`identities.form.tip.*`), lidas no render — constante congela o idioma da
 * importação.
 *
 * Slug × service account (ajustes-front #6, 28/08): os dois tooltips diziam que
 * entravam no SPIFFE ID, e não é verdade — o **slug** é o id da identidade no
 * middleware (`applications/{slug}`, o nome do Deployment/aplicação); quem entra
 * no SPIFFE ID é a **service account** do Kubernetes. Costumam ter o mesmo nome,
 * por isso a SA é pré-preenchida com o slug, mas são chaves de sistemas
 * diferentes. As duas frases seguem separadas no catálogo por essa razão.
 */

export interface IdentityFormProps {
  identidade: Identity;
  control: Control<IdentityFormValues>;
  errors: FieldErrors<IdentityFormValues>;
  papeis: IdentityRole[];
  /**
   * Usuários da aplicação, as opções do **Dono**. Vem vazia para quem não pode
   * chamar `listUsers` (approver) — o combobox ainda oferece o «(você)».
   */
  usuarios: ApplicationUser[];
  /** Prévia do SPIFFE ID conforme a digitação (FR-047). */
  spiffePrevisto: string | null;
  /**
   * O template do site usa `{service_account}` (02/10): só então a Service Account é
   * obrigatória e congela com o resto do ID. Vem da prévia do servidor — a tela não
   * conhece o template por outro caminho.
   */
  serviceAccountInTemplate: boolean;
  /** `{registry}/{owner}` configurado — ajuda do campo Imagem (FR-046). */
  prefixoDoRegistry: string | null;
  /** Criar papel na hora pelo combobox (FR-052). */
  onCriarPapel: (nome: string) => void;
  criandoPapel?: boolean;
  /**
   * `true` para quem não pode salvar (viewer). O FR-010 manda **omitir** o botão
   * de escrita, e isso já era feito — mas o formulário continuava editável, então
   * a pessoa digitava e descobria depois que não havia como gravar. Modo leitura
   * é o formulário inteiro, não só o botão.
   */
  somenteLeitura?: boolean;
  /**
   * O namespace como está no formulário. Enquanto ele difere do gravado, o campo
   * **Ambiente efetivo** mostra a prévia que o SERVIDOR calcula
   * (`getIdentityEnvironmentPreview`, Lote 3 · F0-b) — antes deste marco era uma
   * cópia da regra no cliente, que só alcançava dois dos quatro elos da D-6.
   */
  namespaceDigitado: string;
  /**
   * Opções do «Ambiente declarado» — os ambientes SELECIONÁVEIS do cadastro do
   * tenant (Lote 3 · F1-c). Eram três valores fixos no corpo deste componente.
   *
   * Entram como PROP, e não de um `useEnvironmentsSelectable()` aqui dentro, para
   * este componente continuar sem leitura de servidor: ele já recebe papéis,
   * usuários e a prévia do SPIFFE do drawer, e é o que o mantém testável sem MSW.
   *
   * Vazia é estado legítimo (cadastro a carregar, ou tenant sem ambiente): o campo
   * fica só com «não declarado», e o schema recusa qualquer outro valor — a mesma
   * lista nos dois lugares.
   */
  environmentOptions: { value: string; label: string }[];
}

export function IdentityForm({
  identidade,
  control,
  errors,
  papeis,
  usuarios,
  spiffePrevisto,
  serviceAccountInTemplate,
  prefixoDoRegistry,
  onCriarPapel,
  criandoPapel,
  somenteLeitura = false,
  namespaceDigitado,
  environmentOptions,
}: IdentityFormProps) {
  const congelada = identidade.frozen;
  const helpFrozen = congelada
    ? textFrozen(identidade.frozen_at_contract_version)
    : undefined;

  /**
   * A ajuda do campo, com o chip de origem na frente quando o valor foi
   * pré-preenchido pela plataforma (item 18).
   *
   * O chip vai no `help` — que já aceita `ReactNode` — em vez de num prop novo do
   * `Field`: ele é informação **sobre o valor**, não sobre o rótulo, e fica onde o
   * `aria-describedby` do campo já aponta. Um prop novo no `Field` mudaria um
   * componente compartilhado por todas as telas por causa de uma.
   */
  const ajuda = (campo: string, stored: string | null | undefined, texto?: ReactNode) => {
    const origem = fieldOrigin(identidade, campo, stored);
    if (!origem) return texto;
    return (
      <>
        <Chip className={s.chipOrigem}>{originLabel(origem)}</Chip>
        {texto}
      </>
    );
  };
  const roleCurrent = papeis.find((papel) => papel.name === identidade.role);
  // Editado = a tela difere do gravado. Enquanto for igual, o valor e a origem
  // já vieram no `getIdentity`, e perguntar de novo seria pedir ao servidor o que
  // ele acabou de responder.
  const declaredDigitado = useWatch({ control, name: 'ambiente_declarado' }) ?? '';
  const ambienteEditado =
    namespaceDigitado.trim() !== (identidade.namespace ?? '').trim() ||
    declaredDigitado !== (identidade.declared_environment ?? '');
  /**
   * A PRÉVIA VEM DO SERVIDOR (Lote 3 · F0-b).
   *
   * Até aqui esta linha chamava `identityAmbiente`, uma cópia da precedência da
   * D-6 que vivia em `@app/ambiente`. A cópia alcançava só dois dos quatro elos —
   * o declarado e o sufixo do namespace —, e **a label da imagem e a do cluster
   * vêm antes deles**. Por isso a prévia não podia nomear a origem sem arriscar
   * mentir, e a tela caía num «prévia; o servidor confirma ao salvar».
   *
   * Agora quem responde é `getIdentityEnvironmentPreview`, pela mesma função que
   * grava — então a prévia pode dizer «vem da imagem» com a mesma autoridade do
   * valor gravado.
   */
  const previa = useEnvironmentPreview(
    identidade.id,
    { declared: declaredDigitado, namespace: namespaceDigitado },
    ambienteEditado,
  );
  const semAmbiente = t('identities.form.environmentUndetermined');
  // Com a tela editada mas a resposta ainda a caminho, fica o que está gravado:
  // é a última coisa verdadeira que se sabe. Inventar «não determinado» no vão
  // faria o campo piscar para um valor que ninguém afirmou.
  const ambienteEfetivo = ambienteEditado
    ? (previa.data?.environment ?? identidade.environment)
    : // O ambiente de uma IDENTIDADE (eixo A: `dev|hmg|prod`) sai cru, e isso não é
      // regressão: até o Lote 3 esta linha chamava `ambienteLabel`, que só tem chave
      // de catálogo para o rótulo do TENANT (`producao|homologacao`) — o eixo A caía
      // no `return valor` e já aparecia cru. A função servia aos dois porque não
      // fazia nada para este.
      //
      // Fica explícito enquanto o cadastro de ambientes não existe; no F1-c o rótulo
      // passa a ser o `name` do ambiente cadastrado, e aí há o que mostrar.
      identidade.environment;
  const ambienteShown = (ambienteEfetivo ?? '') || semAmbiente;
  /**
   * A linha que diz **de onde veio** o ambiente efetivo (30/08) — a resposta para
   * "por que este campo não muda quando eu mexo no de baixo".
   *
   * Editada ou não, a origem sai da MESMA cadeia: da prévia quando há edição, do
   * `environment_origin` gravado quando não há.
   *
   * Sem `environment_origin` (o servidor não confirmou o elo, ou é uma resposta de
   * `listIdentities`), cai no texto genérico da precedência — dizer a ordem é
   * honesto; nomear uma fonte não confirmada, não. Na prévia isso não acontece: a
   * prévia É o cálculo, então ela sempre nomeia um elo.
   */
  const ambienteOrigem = ambienteEditado
    ? (previa.data?.origin ?? identidade.environment_origin)
    : identidade.environment_origin;
  const ambienteEffectiveHelp =
    ambienteOriginText(ambienteOrigem) ?? t('identities.form.environmentEffective.generic');

  return (
    <>
      <FieldGrid>
        <Field
          label={t('identities.form.slug.label')}
          span={6}
          required
          error={errors.slug?.message ?? null}
          help={ajuda('slug', identidade.slug, helpFrozen ?? t('identities.form.slug.help'))}
          tip={t('identities.form.tip.slug')}
        >
          {(props) => (
            <Controller
              control={control}
              name="slug"
              render={({ field }) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  readOnly={congelada || somenteLeitura}
                  placeholder={t('identities.form.slug.placeholder')}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.role.label')}
          span={6}
          error={errors.papel?.message ?? null}
          help={ajuda('papel', identidade.role, t('identities.form.role.help'))}
          tip={t('identities.form.tip.role')}
        >
          {(props) => (
            <Controller
              control={control}
              name="papel"
              render={({ field }) => (
                <SearchCombobox
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  options={papeis.map((papel) => ({
                    value: papel.name,
                    label: papel.name,
                    hint:
                      papel.origin === 'automatico'
                        ? t('identities.form.role.hintAuto')
                        : undefined,
                  }))}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('identities.form.role.placeholder')}
                  searchPlaceholder={t('identities.form.role.placeholder')}
                  creatable
                  disabled={criandoPapel || somenteLeitura}
                  onCreate={onCriarPapel}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.namespace.label')}
          span={4}
          required
          error={errors.namespace?.message ?? null}
          help={ajuda(
            'namespace',
            identidade.namespace,
            helpFrozen ?? t('identities.form.namespace.help'),
          )}
        >
          {(props) => (
            <Controller
              control={control}
              name="namespace"
              render={({ field }) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  readOnly={congelada || somenteLeitura}
                  placeholder={t('identities.form.namespace.placeholder')}
                />
              )}
            />
          )}
        </Field>

        {/* Ao lado do namespace porque é dele que sai — e porque é o que decide o
            regime de aprovação da próxima release (FR-066): `prod` exige pessoa,
            `dev`/`hmg` fecham sozinhos, e sem ambiente também exige pessoa. Quem
            atribui precisa ver em que regime a identidade cai. Não se digita.

            **«efetivo» e o chip «somente leitura» vêm do ensaio de 30/08.** Este
            campo e o «Ambiente declarado» ao lado tinham rótulos quase iguais e só
            o segundo abre lista; ao vivo, a cena parou aqui. O rótulo separa os
            dois, o chip diz por que este não abre nada, e a linha de apoio diz **de
            onde veio o valor** — que é o que explica por que ele não se mexe quando
            a pessoa mexe no outro. */}
        <Field
          label={t('identities.form.environmentEffective.label')}
          span={4}
          help={
            <>
              <Chip className={s.chipOrigem}>{t('identities.form.readOnlyChip')}</Chip>
              {ambienteEffectiveHelp}
            </>
          }
          tip={t('identities.form.tip.environment')}
        >
          {(props) => (
            <TextInput
              {...props}
              mono
              readOnly
              data-previa={ambienteEditado ? 'true' : undefined}
              data-origem={identidade.environment_origin ?? undefined}
              value={ambienteShown}
              onChange={() => undefined}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.declaredEnvironment.label')}
          span={4}
          error={errors.ambiente_declarado?.message ?? null}
          help={t('identities.form.declaredEnvironment.help')}
          tip={t('identities.form.tip.declaredEnvironment')}
        >
          {(props) => (
            <Controller
              control={control}
              name="ambiente_declarado"
              render={({ field }) => (
                <SearchCombobox
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  // As opções vêm do CADASTRO do tenant (Lote 3 · F1-c), e só as
                  // SELECIONÁVEIS — `useEnvironmentsSelectable` já filtra o eixo C.
                  // Eram três valores fixos aqui.
                  //
                  // O rótulo é o `name` do ambiente com o slug ao lado: o `name` é o
                  // que a pessoa reconhece («Homologação») e o slug é o que ela vai
                  // ver gravado na linha da identidade e no namespace. Mostrar só um
                  // dos dois obriga a saber o mapeamento de cabeça.
                  options={[
                    { value: '', label: t('identities.form.declaredEnvironment.none') },
                    ...environmentOptions,
                  ]}
                  value={field.value}
                  onChange={(valor) => field.onChange(valor ?? '')}
                  placeholder={t('identities.form.declaredEnvironment.none')}
                  searchPlaceholder={t('identities.form.declaredEnvironment.searchPlaceholder')}
                  disabled={somenteLeitura}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.workload.label')}
          span={4}
          required
          error={errors.workload?.message ?? null}
          help={ajuda(
            'workload',
            identidade.workload,
            helpFrozen ?? t('identities.form.workload.help'),
          )}
          tip={t('identities.form.tip.workload')}
        >
          {(props) => (
            <Controller
              control={control}
              name="workload"
              render={({ field }) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  readOnly={congelada || somenteLeitura}
                  placeholder={t('identities.form.workload.placeholder')}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.serviceAccount.label')}
          span={4}
          required={serviceAccountInTemplate}
          error={errors.service_account?.message ?? null}
          tip={t('identities.form.tip.serviceAccount')}
        >
          {(props) => (
            <Controller
              control={control}
              name="service_account"
              render={({ field }) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  readOnly={(congelada && serviceAccountInTemplate) || somenteLeitura}
                  placeholder={t('identities.form.serviceAccount.placeholder')}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.image.label')}
          span={12}
          required
          error={errors.imagem?.message ?? null}
          help={
            helpFrozen ??
            (prefixoDoRegistry
              ? t('identities.form.image.helpWithPrefix', { prefix: prefixoDoRegistry })
              : t('identities.form.image.help'))
          }
          tip={t('identities.form.tip.image')}
        >
          {(props) => (
            <Controller
              control={control}
              name="imagem"
              render={({ field }) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  readOnly={congelada || somenteLeitura}
                  placeholder={t('identities.form.image.placeholder')}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.specialty.label')}
          span={6}
          error={errors.especialidade?.message ?? null}
          help={t('identities.form.specialty.help')}
        >
          {(props) => (
            <Controller
              control={control}
              name="especialidade"
              render={({ field }) => (
                <TextInput
                  {...props}
                  {...field}
                  readOnly={somenteLeitura}
                  placeholder={t('identities.form.specialty.placeholder')}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.owner.label')}
          span={6}
          required
          help={ajuda('dono', identidade.owner, t('identities.form.owner.help'))}
          tip={t('identities.form.tip.owner')}
          error={errors.dono?.message ?? null}
        >
          {(props) => (
            <Controller
              control={control}
              name="dono"
              render={({ field }) => (
                // O mesmo componente do dono do resource (T7): quem está logado é
                // a primeira opção, com «(você)».
                <OwnerCombobox
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  users={usuarios}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={somenteLeitura}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('identities.form.description.label')}
          span={12}
          error={errors.descricao?.message ?? null}
        >
          {(props) => (
            <Controller
              control={control}
              name="descricao"
              render={({ field }) => (
                <TextArea
                  {...props}
                  {...field}
                  rows={2}
                  readOnly={somenteLeitura}
                  placeholder={t('identities.form.description.placeholder')}
                />
              )}
            />
          )}
        </Field>
      </FieldGrid>

      <dl className={s.leitura}>
        <dt>
          {t('identities.form.spiffeId.label')}{' '}
          <InfoTip text={t('identities.form.tip.spiffe')} size="xs" />
        </dt>
        <dd className={s.mono}>
          {spiffePrevisto ?? (
            <span className={s.vazio}>{t('identities.form.spiffeId.empty')}</span>
          )}
        </dd>

        <dt>{t('identities.form.kind.label')}</dt>
        <dd>
          <Chip>{t('identities.vocab.agent')}</Chip>
        </dd>

        <dt>{t('identities.form.roleCurrent.label')}</dt>
        <dd>
          {identidade.role ? (
            <span className={s.papel}>
              <Chip>{identidade.role}</Chip>
              {roleCurrent?.origin === 'automatico' ? (
                <InfoTip
                  text={t('identities.form.tip.roleAuto')}
                  label={t('identities.form.roleCurrent.tipLabel')}
                  size="xs"
                />
              ) : null}
            </span>
          ) : (
            <span className={s.vazio}>{t('identities.form.roleCurrent.none')}</span>
          )}
        </dd>

        <dt>{t('identities.form.discoveredAt.label')}</dt>
        <dd>{dataHour(identidade.discovered_at) ?? '—'}</dd>

        <dt>{t('identities.form.lastRelease.label')}</dt>
        <dd>
          {dataHour(identidade.last_release_seen_at) ?? t('identities.form.lastRelease.none')}
          {/*
            Tri-estado, e só `false` fala: `null` é "ninguém perguntou"
            (`listIdentities` não lê releases por linha) e `true` não tem o que
            dizer — a release declarou, e a pendência dela está na fila.
          */}
          {identidade.last_release_has_declaration === false ? (
            <span className={s.releaseSemDeclaracao}>
              <Chip icon="warning">{releaseWithoutDeclarationLabel()}</Chip>
              <InfoTip text={releaseWithoutDeclarationTip()} size="xs" />
            </span>
          ) : null}
        </dd>

        <dt>{t('identities.form.activeContract.label')}</dt>
        <dd>
          {identidade.active_contract_version != null ? (
            `v${identidade.active_contract_version}`
          ) : (
            <span className={s.vazio}>{t('identities.form.activeContract.none')}</span>
          )}
        </dd>
      </dl>
    </>
  );
}
